import { type Dispatch, useCallback, useEffect, useReducer } from "react";

import { isInIframe } from "./embed-detection";
import {
  createSecureMessageListener,
  getParentPostMessageOrigin,
  sendSecurePostMessage,
} from "./postmessage-validation";
import { WidgetApiError } from "./widget-api-error";
import {
  AUTH_TOKEN_REQUEST_MESSAGE_TYPE,
  AUTH_TOKEN_RESPONSE_MESSAGE_TYPE,
  AUTH_TOKEN_TIMEOUT_MS,
  type AuthTokenMessage,
  getNextAuthTokenRefreshDelayMs,
} from "./widget-auth-protocol";

const AUTH_TOKEN_TIMEOUT_ERROR_MESSAGE =
  "Timed out waiting for the host app to provide an auth token.";
const AUTH_TOKEN_MISSING_ERROR_MESSAGE =
  "The host app did not provide an auth token for this widget.";

type AuthRequestReason = "initial" | "refresh";

export interface WidgetConfigAuthState {
  authToken: string | null;
  authTokenVersion: number;
  hasRequestedAuthToken: boolean;
  authRequestNonce: number;
  authRequestReason: AuthRequestReason | null;
  authErrorMessage: string | null;
  isRequestingAuthToken: boolean;
}

type AuthAction =
  | { type: "request-start"; reason: AuthRequestReason }
  | { type: "request-timeout"; errorMessage: string }
  | {
      type: "request-resolved";
      resolvedAuthToken: ReturnType<typeof resolveRequestedAuthToken>;
    };

const initialAuthState: WidgetConfigAuthState = {
  authToken: null,
  authTokenVersion: 0,
  hasRequestedAuthToken: false,
  authRequestNonce: 0,
  authRequestReason: null,
  authErrorMessage: null,
  isRequestingAuthToken: false,
};

function authReducer(state: WidgetConfigAuthState, action: AuthAction): WidgetConfigAuthState {
  switch (action.type) {
    case "request-start":
      return {
        ...state,
        hasRequestedAuthToken: action.reason === "initial" ? true : state.hasRequestedAuthToken,
        authErrorMessage: null,
        authRequestReason: action.reason,
        isRequestingAuthToken: true,
        authRequestNonce: state.authRequestNonce + 1,
      };
    case "request-timeout":
      return {
        ...state,
        authRequestReason: null,
        isRequestingAuthToken: false,
        authToken: null,
        authErrorMessage: action.errorMessage,
      };
    case "request-resolved":
      return {
        ...state,
        authRequestReason: null,
        isRequestingAuthToken: false,
        authErrorMessage: action.resolvedAuthToken.authErrorMessage,
        authToken: action.resolvedAuthToken.authToken,
        authTokenVersion:
          state.authTokenVersion + (action.resolvedAuthToken.shouldIncrementTokenVersion ? 1 : 0),
      };
  }
}

function isAuthTokenMessage(data: unknown): data is AuthTokenMessage {
  if (!data || typeof data !== "object") {
    return false;
  }

  const message = data as Partial<AuthTokenMessage>;

  return (
    message.type === AUTH_TOKEN_RESPONSE_MESSAGE_TYPE &&
    typeof message.data === "object" &&
    message.data !== null &&
    typeof message.data.token === "string"
  );
}

export function resolveRequestedAuthToken(token: string) {
  const nextToken = token.trim();
  if (!nextToken) {
    return {
      authErrorMessage: AUTH_TOKEN_MISSING_ERROR_MESSAGE,
      authToken: null,
      shouldIncrementTokenVersion: false,
    };
  }

  return {
    authErrorMessage: null,
    authToken: nextToken,
    shouldIncrementTokenVersion: true,
  };
}

export function shouldScheduleAuthRefresh({
  authErrorMessage,
  authToken,
  error,
  isEmbedded,
  isLoading,
  isRequestingAuthToken,
  serverConfig,
}: {
  authErrorMessage: string | null;
  authToken: string | null;
  error: unknown;
  isEmbedded: boolean;
  isLoading: boolean;
  isRequestingAuthToken: boolean;
  serverConfig: unknown;
}) {
  return (
    Boolean(authToken) &&
    Boolean(serverConfig) &&
    !isLoading &&
    !error &&
    isEmbedded &&
    !authErrorMessage &&
    !isRequestingAuthToken
  );
}

export function hasWidgetAuthFailure({
  authErrorMessage,
  authRequestError,
  authToken,
  error,
  hasRequestedAuthToken,
  isEmbedded,
}: {
  authErrorMessage: string | null;
  authRequestError: WidgetApiError | null;
  authToken: string | null;
  error: unknown;
  hasRequestedAuthToken: boolean;
  isEmbedded: boolean;
}) {
  return (
    Boolean(authErrorMessage) ||
    (Boolean(authRequestError) && !isEmbedded) ||
    Boolean(
      error instanceof WidgetApiError &&
      [401, 403, 503].includes(error.status) &&
      (hasRequestedAuthToken || Boolean(authToken)),
    )
  );
}

export type WidgetConfigAuthQueryOptions = {
  enabled?: boolean;
  token?: string;
  tokenVersion?: number;
};

export type WidgetConfigAuthQueryResult<TConfig> = {
  data: TConfig | undefined;
  isLoading: boolean;
  error: unknown;
};

export function useWidgetConfigAuthState() {
  return useReducer(authReducer, initialAuthState);
}

export function useWidgetConfigAuthEffects({
  authState,
  dispatchAuth,
  configQueryError,
  isConfigLoading,
  serverConfig,
}: {
  authState: WidgetConfigAuthState;
  dispatchAuth: Dispatch<AuthAction>;
  configQueryError: unknown;
  isConfigLoading: boolean;
  serverConfig: unknown;
}) {
  const isEmbedded = isInIframe();
  const {
    authToken,
    authTokenVersion,
    hasRequestedAuthToken,
    authRequestNonce,
    authRequestReason,
    authErrorMessage,
    isRequestingAuthToken,
  } = authState;

  const authRequestError =
    configQueryError instanceof WidgetApiError && [401, 403].includes(configQueryError.status)
      ? configQueryError
      : null;
  const shouldRequestInitialAuthToken =
    Boolean(authRequestError) &&
    !hasRequestedAuthToken &&
    !authToken &&
    !authErrorMessage &&
    isEmbedded &&
    !isRequestingAuthToken;

  const requestAuthToken = useCallback(
    (reason: AuthRequestReason) => {
      if (!isEmbedded || isRequestingAuthToken) {
        return;
      }

      dispatchAuth({ type: "request-start", reason });
    },
    [dispatchAuth, isEmbedded, isRequestingAuthToken],
  );

  useEffect(() => {
    if (!shouldRequestInitialAuthToken) {
      return;
    }

    requestAuthToken("initial");
  }, [requestAuthToken, shouldRequestInitialAuthToken]);

  useEffect(() => {
    if (!authRequestNonce || !authRequestReason) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      dispatchAuth({
        type: "request-timeout",
        errorMessage: AUTH_TOKEN_TIMEOUT_ERROR_MESSAGE,
      });
    }, AUTH_TOKEN_TIMEOUT_MS);

    const handleMessage = createSecureMessageListener<AuthTokenMessage>(
      (message) => {
        const resolvedAuthToken = resolveRequestedAuthToken(message.data.token);
        window.clearTimeout(timeoutId);
        dispatchAuth({ type: "request-resolved", resolvedAuthToken });
      },
      {
        expectedTypes: [AUTH_TOKEN_RESPONSE_MESSAGE_TYPE],
        validateData: isAuthTokenMessage,
      },
    );

    window.addEventListener("message", handleMessage);
    sendSecurePostMessage(
      window.parent,
      { type: AUTH_TOKEN_REQUEST_MESSAGE_TYPE },
      getParentPostMessageOrigin(),
    );

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener("message", handleMessage);
    };
  }, [authRequestNonce, authRequestReason, dispatchAuth]);

  const shouldRefreshAuthToken = shouldScheduleAuthRefresh({
    authErrorMessage,
    authToken,
    error: configQueryError,
    isEmbedded,
    isLoading: isConfigLoading,
    isRequestingAuthToken,
    serverConfig,
  });

  useEffect(() => {
    if (!shouldRefreshAuthToken || !authToken) {
      return;
    }

    // Re-schedule after each successful token response, even when the host returns the same JWT.
    void authTokenVersion;
    const refreshDelayMs = getNextAuthTokenRefreshDelayMs(authToken);
    const timeoutId = window.setTimeout(() => {
      requestAuthToken("refresh");
    }, refreshDelayMs);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [authToken, authTokenVersion, requestAuthToken, shouldRefreshAuthToken]);

  const isAwaitingAuthToken =
    shouldRequestInitialAuthToken ||
    (authRequestReason === "initial" && isRequestingAuthToken) ||
    (hasRequestedAuthToken && !authToken && !authErrorMessage);
  const hasAuthFailureResult = hasWidgetAuthFailure({
    authErrorMessage,
    authRequestError,
    authToken,
    error: configQueryError,
    hasRequestedAuthToken,
    isEmbedded,
  });

  return {
    isAwaitingAuthToken,
    hasAuthFailure: hasAuthFailureResult,
  };
}

export function useWidgetConfigAuth<TConfig>({
  useConfigQuery,
}: {
  useConfigQuery: (options: WidgetConfigAuthQueryOptions) => WidgetConfigAuthQueryResult<TConfig>;
}) {
  const [authState, dispatchAuth] = useWidgetConfigAuthState();
  const { authToken, authTokenVersion } = authState;

  const {
    data: serverConfig,
    isLoading: isConfigLoading,
    error: configQueryError,
  } = useConfigQuery({
    token: authToken ?? undefined,
    tokenVersion: authTokenVersion,
  });

  const { isAwaitingAuthToken, hasAuthFailure } = useWidgetConfigAuthEffects({
    authState,
    dispatchAuth,
    configQueryError,
    isConfigLoading,
    serverConfig,
  });

  return {
    serverConfig,
    isConfigLoading,
    configQueryError,
    isAwaitingAuthToken,
    hasAuthFailure,
  };
}
